import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Activity, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getActivity, getLastSeen, markSeen, type ActivityEvent } from "@/lib/activityStore";

interface Props {
  projectId: string;
  currentUserId?: string;
}

const VERB_LABELS: Record<string, string> = {
  created: "created",
  updated: "updated",
  commented: "commented on",
  comment_edited: "edited a comment on",
  comment_deleted: "deleted a comment on",
  resolved: "resolved a comment on",
  reopened: "reopened a comment on",
  invited: "invited",
  role_changed: "changed role for",
  removed: "removed",
  left: "left the project",
};

function describe(e: ActivityEvent): string {
  const verb = VERB_LABELS[e.verb] || e.verb;
  if (e.verb === "left") return `${e.actor_name || "Someone"} left the project`;
  const label = e.entity_label ? `"${e.entity_label}"` : e.entity_type;
  return `${e.actor_name || "Someone"} ${verb} ${label}`;
}

export default function ActivityFeedPanel({ projectId, currentUserId }: Props) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const lastSeenAtMount = useRef<string | null>(
    currentUserId ? getLastSeen(projectId, currentUserId) : null
  );

  const load = async (before?: string) => {
    try {
      const data = await getActivity(projectId, before, 50);
      setEvents((prev) => (before ? [...prev, ...data] : data));
      setHasMore(data.length === 50);
    } catch {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
    return () => {
      if (currentUserId) markSeen(projectId, currentUserId);
    };
  }, [projectId]);

  const handleLoadMore = () => {
    if (events.length === 0) return;
    setLoadingMore(true);
    load(events[events.length - 1].created_at);
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl">
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
          <Activity className="h-4 w-4" /> Activity
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">Who changed what, across every tab</p>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : events.length === 0 ? (
        <div className="text-center py-12">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No activity yet</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {events.map((e, i) => {
            const isNew = !!lastSeenAtMount.current && e.created_at > lastSeenAtMount.current;
            return (
              <div
                key={`${e.created_at}-${i}`}
                className="flex items-start gap-2 px-3 py-2 rounded-md border border-border bg-card text-xs"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-foreground">
                    {describe(e)}
                    {isNew && (
                      <span className="ml-1.5 text-[9px] font-mono uppercase tracking-wider text-accent">new</span>
                    )}
                  </p>
                  {e.summary && e.verb === "commented" && (
                    <p className="text-muted-foreground mt-0.5 line-clamp-2">"{e.summary}"</p>
                  )}
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 font-mono">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
            );
          })}
          {hasMore && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="w-full mt-2 gap-1.5"
            >
              {loadingMore && <Loader2 className="h-3 w-3 animate-spin" />} Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
